<template>
  <div class="camera-test-page">
    <h2>iPhone 摄像头测试页</h2>

    <div class="hint">
      <p>测试顺序建议：</p>
      <p>1. 先点“申请权限并枚举”</p>
      <p>2. 先测试“不指定分辨率” + “直接试后摄 environment”</p>
      <p>3. 再切换不同 cameraId 看哪个视野最广</p>
      <p>4. 最后测试 720 / 1080 是否造成额外裁切</p>
    </div>

    <div class="toolbar">
      <button @click="requestPermissionAndList">1. 申请权限并枚举</button>
      <button @click="startWithEnvironment">直接试后摄 environment</button>
      <button @click="stopStream">停止</button>
    </div>

    <div class="toolbar">
      <select v-model="selectedDeviceId">
        <option value="">请选择摄像头</option>
        <option
          v-for="(cam, index) in cameras"
          :key="cam.deviceId || index"
          :value="cam.deviceId"
        >
          {{ index + 1 }}. {{ cam.label || '未命名摄像头' }} | {{ cam.deviceId }}
        </option>
      </select>

      <button @click="startWithSelectedCamera" :disabled="!selectedDeviceId">
        2. 启动选中摄像头
      </button>
    </div>

    <div class="toolbar">
      <button
        :class="{ active: currentMode === 'none' }"
        @click="setMode('none')"
      >
        不指定分辨率
      </button>
      <button
        :class="{ active: currentMode === '720' }"
        @click="setMode('720')"
      >
        请求 720x1280
      </button>
      <button
        :class="{ active: currentMode === '1080' }"
        @click="setMode('1080')"
      >
        请求 1080x1920
      </button>
    </div>

    <div class="video-wrap">
      <video
        ref="video"
        autoplay
        playsinline
        muted
      ></video>
    </div>

    <div class="meta">
      <div><strong>当前 origin：</strong>{{ origin }}</div>
      <div><strong>isSecureContext：</strong>{{ isSecureContext }}</div>
      <div><strong>mediaDevices 可用：</strong>{{ mediaDevicesAvailable }}</div>
      <div><strong>当前模式：</strong>{{ currentMode }}</div>
    </div>

    <div class="actions">
      <button @click="copyLog">复制日志</button>
      <button @click="clearLog">清空日志</button>
    </div>

    <pre class="log">{{ logText }}</pre>
  </div>
</template>

<script>
export default {
  name: 'CameraTest',
  data() {
    return {
      cameras: [],
      selectedDeviceId: '',
      currentStream: null,
      currentMode: 'none', // none | 720 | 1080
      logText: '',
      origin: '',
      isSecureContext: false,
      mediaDevicesAvailable: false
    }
  },
  mounted() {
    this.origin = window.location.origin
    this.isSecureContext = window.isSecureContext
    this.mediaDevicesAvailable = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)

    this.appendLog({
      step: 'page_mounted',
      origin: this.origin,
      isSecureContext: this.isSecureContext,
      mediaDevicesAvailable: this.mediaDevicesAvailable,
      userAgent: navigator.userAgent
    })
  },
  beforeDestroy() {
    this.stopStream()
  },
  methods: {
    appendLog(data) {
      const text =
        typeof data === 'string'
          ? data
          : JSON.stringify(data, null, 2)

      this.logText = `${new Date().toLocaleString()}\n${text}\n\n${this.logText}`
      console.log(data)
    },

    clearLog() {
      this.logText = ''
    },

    async copyLog() {
      try {
        await navigator.clipboard.writeText(this.logText)
        this.appendLog('日志已复制到剪贴板')
      } catch (e) {
        this.appendLog({
          error: 'copy_failed',
          name: e.name,
          message: e.message
        })
      }
    },

    setMode(mode) {
      this.currentMode = mode
      this.appendLog({
        step: 'set_mode',
        currentMode: mode
      })
    },

    stopStream() {
      if (this.currentStream) {
        this.currentStream.getTracks().forEach(track => track.stop())
        this.currentStream = null
      }

      if (this.$refs.video) {
        this.$refs.video.srcObject = null
      }

      this.appendLog({
        step: 'stream_stopped'
      })
    },

    buildVideoConstraints(base = {}) {
      if (this.currentMode === '720') {
        return {
          ...base,
          width: { ideal: 720 },
          height: { ideal: 1280 }
        }
      }

      if (this.currentMode === '1080') {
        return {
          ...base,
          width: { ideal: 1080 },
          height: { ideal: 1920 }
        }
      }

      return base
    },

    async requestPermissionAndList() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.appendLog({
          error: 'mediaDevices_not_available',
          origin: window.location.origin,
          isSecureContext: window.isSecureContext
        })
        return
      }

      this.stopStream()

      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        })

        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoInputs = devices.filter(d => d.kind === 'videoinput')

        this.cameras = videoInputs
        if (videoInputs.length > 0) {
          this.selectedDeviceId = videoInputs[0].deviceId
        }

        const firstTrack = tempStream.getVideoTracks()[0]
        const settings = firstTrack.getSettings ? firstTrack.getSettings() : {}
        const constraints = firstTrack.getConstraints ? firstTrack.getConstraints() : {}
        const capabilities = firstTrack.getCapabilities ? firstTrack.getCapabilities() : {}

        this.appendLog({
          step: 'permission_granted_and_devices_listed',
          deviceCount: videoInputs.length,
          devices: videoInputs.map((d, i) => ({
            index: i,
            label: d.label,
            deviceId: d.deviceId,
            groupId: d.groupId
          })),
          firstTempTrackSettings: settings,
          firstTempTrackConstraints: constraints,
          firstTempTrackCapabilities: capabilities
        })

        tempStream.getTracks().forEach(track => track.stop())
      } catch (e) {
        this.appendLog({
          error: 'requestPermissionAndList_failed',
          name: e.name,
          message: e.message
        })
      }
    },

    async startWithSelectedCamera() {
      if (!this.selectedDeviceId) {
        this.appendLog('请先选择一个摄像头')
        return
      }

      await this.startWithDeviceId(this.selectedDeviceId)
    },

    async startWithDeviceId(deviceId) {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.appendLog({
          error: 'mediaDevices_not_available',
          origin: window.location.origin,
          isSecureContext: window.isSecureContext
        })
        return
      }

      this.stopStream()

      try {
        const constraints = {
          video: this.buildVideoConstraints({
            deviceId: { exact: deviceId }
          }),
          audio: false
        }

        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        this.currentStream = stream

        if (this.$refs.video) {
          this.$refs.video.srcObject = stream
        }

        const track = stream.getVideoTracks()[0]
        const settings = track.getSettings ? track.getSettings() : {}
        const realConstraints = track.getConstraints ? track.getConstraints() : {}
        const capabilities = track.getCapabilities ? track.getCapabilities() : {}

        this.appendLog({
          step: 'startWithDeviceId_success',
          selectedDeviceId: deviceId,
          requestMode: this.currentMode,
          settings,
          constraints: realConstraints,
          capabilities
        })
      } catch (e) {
        this.appendLog({
          error: 'startWithDeviceId_failed',
          selectedDeviceId: deviceId,
          requestMode: this.currentMode,
          name: e.name,
          message: e.message
        })
      }
    },

    async startWithEnvironment() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.appendLog({
          error: 'mediaDevices_not_available',
          origin: window.location.origin,
          isSecureContext: window.isSecureContext
        })
        return
      }

      this.stopStream()

      try {
        const constraints = {
          video: this.buildVideoConstraints({
            facingMode: { ideal: 'environment' }
          }),
          audio: false
        }

        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        this.currentStream = stream

        if (this.$refs.video) {
          this.$refs.video.srcObject = stream
        }

        const track = stream.getVideoTracks()[0]
        const settings = track.getSettings ? track.getSettings() : {}
        const realConstraints = track.getConstraints ? track.getConstraints() : {}
        const capabilities = track.getCapabilities ? track.getCapabilities() : {}

        this.appendLog({
          step: 'startWithEnvironment_success',
          requestMode: this.currentMode,
          settings,
          constraints: realConstraints,
          capabilities
        })
      } catch (e) {
        this.appendLog({
          error: 'startWithEnvironment_failed',
          requestMode: this.currentMode,
          name: e.name,
          message: e.message
        })
      }
    }
  }
}
</script>

<style scoped>
.camera-test-page {
  min-height: 100vh;
  background: #111;
  color: #f3f3f3;
  padding: 16px;
  box-sizing: border-box;
}

h2 {
  margin: 0 0 12px;
  font-size: 20px;
}

.hint {
  margin-bottom: 16px;
  padding: 12px;
  border-radius: 10px;
  background: #1b1b1b;
  color: #bbb;
  line-height: 1.7;
  font-size: 13px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

button,
select {
  min-height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #444;
  background: #222;
  color: #fff;
  font-size: 14px;
}

button.active {
  border-color: #409eff;
  background: #18314f;
}

button:disabled {
  opacity: 0.5;
}

.video-wrap {
  width: 100%;
  margin-bottom: 12px;
}

video {
  width: 100%;
  max-height: 62vh;
  border-radius: 12px;
  background: #000;
  object-fit: contain;
}

.meta {
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 10px;
  background: #1b1b1b;
  line-height: 1.8;
  font-size: 13px;
  word-break: break-all;
}

.actions {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.log {
  background: #1a1a1a;
  color: #9fe89f;
  padding: 12px;
  border-radius: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
  overflow: auto;
}
</style>